import { SearchFilter } from "src/app/shared";
import { DesignLayout } from "../../design-layouts/models";

import { KeyArrayMap, KeyValueMap } from "@merri/common";
import { DesignLayoutResponse, DesignLayoutsFilterResponse, DesignLayoutsSelectorResponses } from "../../design-layouts";
import { GetVenueResponse, Venue, VenueBasic, VenueBasicResponse, VenuesResponses, VenuesSearchResponses } from "../../venues";
import { VisionboardCategoryResponse, VisionboardItemResponse } from "../../visionboard/responses";
import { ScreenshotLegendPlacement } from "../models/enums/screenshot-legend-placement";
import { VenueAdminReportData } from "../models/venue-admin-report-data";
import { VenueAdminRoom } from "../models/venue-admin-room";
import { VenueAdminVenueInfo } from "../models/venue-admin-venue-info";

export interface PostVenueAdminRoomResponse {
  room: VenueAdminRoomResponse;
  success: boolean;
  errors: string[];
}

export interface PostVenueAdminVenueResponse {
  venue: GetVenueResponse;
  success: boolean;
  errors: string[];
}

export interface PostVenueTemplateDeactivateResponse {
  success: boolean;
  errors: string[];
}

export interface PostVenueTemplateNameResponse {
  success: boolean;
  errors: string[];
}

export interface GetVenueTemplatesResponse {
  templates: DesignLayoutResponse[];
  totalCount: number;
  success: boolean;
  errors: string[];
}

export interface GetVenueTemplatesFiltersResponse {
  filters: DesignLayoutsFilterResponse[];
  success: boolean;
  errors: string[];
}

export interface VenueAdminRoomResponse {
  roomId: number;
  roomName: string;
  primaryImageUrl: string;
  maxCapacity: number;
  distanceBetweenChairsInches: number;
  allowsChandeliers: boolean;
  displayIconLegendInScreenshots: boolean;
  screenshotLegendPlacement: ScreenshotLegendPlacement;
}

export interface GetVenueAdminVenuesResponse {
  venues: VenueBasicResponse[];
  success: boolean;
  errors: string[];
}

export interface GetVenueAdminVenueInfoResponse {
  venue: GetVenueResponse;
  attributes: KeyArrayMap<string>;
  success: boolean;
  errors: string[];
}

export interface GetVenueAdminVenueRoomResponse {
  room: VenueAdminRoomResponse;
  success: boolean;
  errors: string[];
}

export interface GetVenueItemsCategoriesResponse {
  categories: VisionboardCategoryResponse[];
  success: boolean;
  errors: string[];
}

export interface GetVenueItemsResponse {
  items: VisionboardItemResponse[];
  selectedSkuIds: number[];
  success: boolean;
  errors: string[];
}

export interface GetVenueItemsQuantitiesResponse {
  itemQuantities: KeyValueMap<number>;
  hasOverbookedReport?: boolean;
  success: boolean;
  errors: string[];
}

export interface PostVenueItemQuantityResponse {
  itemQuantities: KeyValueMap<number>; // Not used at this time.
  hasOverbookedReport?: boolean;
  success: boolean;
  errors: string[];
}

export interface GetVenueItemQuantitiesReportResponse {
  errors: string[];
  success: boolean;
  downloadKey: string;
  fileName: string;
}

export interface GetVenueAdminReportsResponse {
  errors: string[];
  success: boolean;
  overbookedReportVenueIds: number[];
}

export class VenueAdminResponses {
  static getVenueAdminVenueInfoFromResponse(response: GetVenueAdminVenueInfoResponse): VenueAdminVenueInfo {
    if (!response) {
      return null;
    }

    const venueAdminVenueInfo = new VenueAdminVenueInfo();
    venueAdminVenueInfo.attributes = response.attributes;
    venueAdminVenueInfo.venue = VenuesResponses.getVenue(response.venue);

    return venueAdminVenueInfo;
  }

  static getVenueTemplatesFromResponse(response: GetVenueTemplatesResponse): DesignLayout[] {
    if (!response) {
      return null;
    }

    return response.templates?.map(t => DesignLayoutsSelectorResponses.getDesignLayout(t)) ?? [];
  }

  static getVenueTemplatesFiltersFromResponse(response: GetVenueTemplatesFiltersResponse): [string, SearchFilter[]] {
    if (!response) {
      return null;
    }

    let source: string;
    const searchFilters: SearchFilter[] = [];
    response.filters?.forEach(f => {
      source = f.source;
      searchFilters.push(...DesignLayoutsSelectorResponses.getDesignLayoutFilters(f));
    });

    return [source, searchFilters];
  }

  static getVenueAdminVenuesBasicFromResponse(response: GetVenueAdminVenuesResponse): VenueBasic[] {
    if (!response) {
      return null;
    }

    return response.venues?.map(v => VenueAdminResponses.getVenueAdminVenueBasicFromResponse(v));
  }

  static getVenueAdminVenueBasicFromResponse(response: VenueBasicResponse): VenueBasic {
    if (!response) {
      return null;
    }

    return VenuesSearchResponses.getVenueSearchResult(response);
  }

  static getVenueAdminVenueFromResponse(response: GetVenueResponse): Venue {
    if (!response) {
      return null;
    }

    return VenuesResponses.getVenue(response);
  }

  static getVenueAdminRoomFromResponse(response: VenueAdminRoomResponse): VenueAdminRoom {
    if (!response) {
      return null;
    }

    const venueAdminRoom: VenueAdminRoom = new VenueAdminRoom();
    venueAdminRoom.roomId = response.roomId;
    venueAdminRoom.roomName = response.roomName;
    venueAdminRoom.primaryImageUrl = response.primaryImageUrl;
    venueAdminRoom.maxCapacity = response.maxCapacity;
    venueAdminRoom.distanceBetweenChairsInches = response.distanceBetweenChairsInches;
    venueAdminRoom.allowsChandeliers = response.allowsChandeliers;
    venueAdminRoom.displayIconLegendInScreenshots = response.displayIconLegendInScreenshots;
    venueAdminRoom.screenshotLegendPlacement = response.screenshotLegendPlacement;

    return venueAdminRoom;
  }

  static getVenueAdminReportDataFromResponse(response: GetVenueAdminReportsResponse): VenueAdminReportData {
    if (!response) {
      return null;
    }

    const venueAdminReportData = new VenueAdminReportData();
    venueAdminReportData.overbookedReportVenueIds = response.overbookedReportVenueIds;

    return venueAdminReportData;
  }
}
